import useSession from 'components/auth/useSession'
import { useFlags } from 'config/useFlagsContext'
import { useCountNotificacaoQuery } from 'graphql/hooks.generated'
import { TipoTopicoNotificacaoEnum } from 'graphql/types.generated'
import useAtmosphere from 'hooks/useAtmosphere'
import { isEqual } from 'lodash'
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import {
  defaultCountTipoNotificacaoRecord,
  NotificacaoCountContextModel,
  NotificacaoDeltaAtmosphereResponseList,
} from './model-notificacaoContext'

const NotificacaoCountContext = createContext<NotificacaoCountContextModel>(defaultCountTipoNotificacaoRecord)

interface NotificacaoCountProviderProps {
  children: React.ReactNode
}

export function NotificacaoCountProvider(props: Readonly<NotificacaoCountProviderProps>) {
  const { children } = props
  const [notificacoes, setNotificacoes] = useState(defaultCountTipoNotificacaoRecord)

  const {
    data: { acesso },
  } = useSession()
  const { NOTIFICACAO_CUIDADO_COMPARTILHADO_ENABLED } = useFlags()
  const tiposTopicosQuery = Object.values(TipoTopicoNotificacaoEnum)

  const {
    data: { resumoNotificacoesNaoVistas },
  } = useCountNotificacaoQuery({
    variables: { tiposTopicos: tiposTopicosQuery },
    context: { newRequest: true },
    pollInterval: 180000, // 3 minutos
    fetchPolicy: 'network-only',
    skip: !NOTIFICACAO_CUIDADO_COMPARTILHADO_ENABLED,
  })

  const notificacoesRef = useRef(notificacoes)

  useEffect(() => {
    notificacoesRef.current = notificacoes
  }, [notificacoes])

  useEffect(() => {
    if (resumoNotificacoesNaoVistas) {
      const mapped = resumoNotificacoesNaoVistas.reduce<NotificacaoCountContextModel>(
        (acc, { tipoTopico, count }) => {
          acc[tipoTopico] = count
          return acc
        },
        { ...defaultCountTipoNotificacaoRecord }
      )

      if (!isEqual(mapped, notificacoesRef.current)) {
        setNotificacoes(mapped)
      }
    }
  }, [resumoNotificacoesNaoVistas])

  const handleMessage = useCallback(
    (mensagens: NotificacaoDeltaAtmosphereResponseList) => {
      if (NOTIFICACAO_CUIDADO_COMPARTILHADO_ENABLED) {
        const novaContagem: NotificacaoCountContextModel = {
          ...notificacoes,
        }

        mensagens.forEach(({ operacao, valor, tipoNotificacao }) => {
          const atual = novaContagem[tipoNotificacao] ?? 0

          const atualizado = (() => {
            switch (operacao) {
              case 'ADD':
                return atual + valor
              case 'SUB':
                return Math.max(0, atual - valor)
              default:
                return atual
            }
          })()

          novaContagem[tipoNotificacao] = atualizado
        })

        setNotificacoes(novaContagem)
      }
    },
    [NOTIFICACAO_CUIDADO_COMPARTILHADO_ENABLED, notificacoes]
  )

  useAtmosphere<NotificacaoDeltaAtmosphereResponseList>({
    topic: `notificacao/${acesso?.id}`,
    onMessage: handleMessage,
  })

  const value = useMemo(() => ({ ...notificacoes }), [notificacoes])

  return <NotificacaoCountContext.Provider value={value}>{children}</NotificacaoCountContext.Provider>
}

export const useNotificacaoCountContext = () => useContext(NotificacaoCountContext)
